import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BadgeCheck, CheckCircle2, Clock, Landmark } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState, ErrorState, LoadingState, Notice, Spinner } from "@/components/common/States";
import { ActionDot, useUnreadNotifications } from "@/components/layout/ActionDot";
import { api, errorMessage, fetcher } from "@/lib/api";
import { rupiah } from "@/lib/labels";

const waktu = (iso) => (iso ? `${new Date(iso).toLocaleString("id-ID", { timeZone: "Asia/Jakarta" })} WIB` : "—");
const uang = (v) => (v === undefined || v === null ? "—" : rupiah(v));

const Kotak = ({ label, value, hint, tone = "text-[#0B3D91]", testId }) => (
  <div className="rounded-2xl border bg-card p-4" data-testid={testId}>
    <p className={`font-brand text-2xl font-extrabold tabular-nums ${tone}`}>{value}</p>
    <p className="text-xs font-medium">{label}</p>
    {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
  </div>
);

// Dua antrean yang menunggu founder: aktivasi akun (pasokan dari reseller) dan
// verifikasi setoran kulakan. Dibuat satu layar supaya follow-up tidak tersebar di banyak tab.
export default function BillingActionQueue({ periode, wholesale, onActivate }) {
  const qc = useQueryClient();
  const belumDibaca = useUnreadNotifications();
  const [busy, setBusy] = useState("");

  const aktivasi = useQuery({
    queryKey: ["billing", "activation", periode],
    queryFn: () => fetcher(`/platform/billing/activation-requests?periode=${periode}`),
    enabled: !!periode,
    refetchInterval: 15000,
  });
  const setoran = useQuery({
    queryKey: ["billing", "settlements-pending", periode],
    queryFn: () => fetcher(`/platform/billing/settlements?periode=${periode}`),
    enabled: !!periode,
    refetchInterval: 15000,
  });

  const tarik = () => qc.invalidateQueries({ queryKey: ["billing"] });
  const verifikasi = async (s) => {
    setBusy(s.id);
    try {
      const { data } = await api.post(`/platform/billing/settlements/${s.id}/verify`);
      toast.success(`Setoran ${rupiah(s.amount_idr)} dari ${s.reseller_name} diverifikasi. Sisa kulakan ${rupiah(data.gambaran.sisa_kulakan)}.`);
      tarik();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy("");
    }
  };

  const a = aktivasi.data?.totals;
  const menungguSetor = (setoran.data?.setoran || []).filter((s) => s.status === "menunggu_verifikasi");
  const nilaiSetor = menungguSetor.reduce((n, s) => n + (s.amount_idr || 0), 0);
  const adaNotif = (kind, teks) => belumDibaca.some((n) => n.kind === kind && (n.body || "").includes(teks));
  const aktivasiBaru = (nama) => adaNotif("billing_aktivasi", nama);
  const setoranBaru = (s) => adaNotif("billing_setoran", s.rincian?.school_name || s.reseller_name);

  return (
    <div className="space-y-6">
      <section className="rounded-2xl border bg-card p-5" data-testid="billing-queue-flow">
        <h3 className="text-sm font-bold">Alur pembayaran akun sekolah</h3>
        <ol className="mt-3 grid gap-2 md:grid-cols-5">
          {[
            { n: 1, judul: "Sekolah bayar", isi: "Sekolah menyetor ke QRIS reseller." },
            { n: 2, judul: "Reseller konfirmasi", isi: "Reseller menandai uang sudah masuk ke dia." },
            { n: 3, judul: "Founder aktifkan", isi: "Akun diaktifkan — antrean di bawah.", aktif: true },
            { n: 4, judul: "Reseller setor kulakan", isi: `${rupiah(wholesale)}/akun ke founder.` },
            { n: 5, judul: "Founder verifikasi setoran", isi: "Cocokkan mutasi rekening — antrean di bawah.", aktif: true },
          ].map((s) => (
            <li key={s.n} className={`rounded-xl border p-3 ${s.aktif ? "border-[#0B3D91] bg-blue-50/60 dark:bg-blue-950/30" : "bg-muted/20"}`}
              data-testid={`billing-queue-step-${s.n}`}>
              <span className={`inline-grid h-6 w-6 place-items-center rounded-full text-xs font-bold ${s.aktif ? "bg-[#0B3D91] text-white" : "bg-muted text-muted-foreground"}`}>{s.n}</span>
              <p className="mt-1.5 text-sm font-semibold">{s.judul}</p>
              <p className="text-xs text-muted-foreground">{s.isi}</p>
            </li>
          ))}
        </ol>
      </section>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <Kotak label="Akun menunggu aktivasi" value={a?.akun ?? "—"} hint="Sudah dikonfirmasi reseller"
          tone="text-amber-600" testId="billing-queue-stat-akun" />
        <Kotak label="Nilai jual akun itu" value={uang(a?.nominal_jual)} hint={`${a?.sekolah ?? 0} sekolah`}
          testId="billing-queue-stat-nilai" />
        <Kotak label="Setoran menunggu verifikasi" value={menungguSetor.length} hint={uang(nilaiSetor)}
          tone="text-amber-600" testId="billing-queue-stat-setoran" />
        <Kotak label="Sekolah menunggu aktivasi" value={a?.sekolah ?? "—"} hint={`${a?.dari_reseller ?? 0} dengan permintaan reseller`}
          tone="text-emerald-600" testId="billing-queue-stat-sekolah" />
      </div>

      <section className="rounded-2xl border bg-card p-6" data-testid="billing-queue-aktivasi">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="flex items-center gap-2 text-base font-bold">
              <BadgeCheck className="h-4 w-4 text-[#0B3D91]" aria-hidden /> 1. Aktifkan akun yang sudah dibayar
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Akun di bawah sudah dikonfirmasi reseller sebagai sudah dibayar sekolah. Aktifkan agar tidak terkena
              penguncian tanggal dan tercatat lunas ke founder. Hanya akun berstatus “sudah dikonfirmasi” yang berubah.
            </p>
          </div>
        </div>
        {aktivasi.isLoading ? <LoadingState rows={3} /> : aktivasi.error ? (
          <ErrorState error={aktivasi.error} onRetry={aktivasi.refetch} />
        ) : !aktivasi.data?.rows.length ? (
          <EmptyState title="Tidak ada akun menunggu aktivasi"
            description="Begitu reseller mengonfirmasi pembayaran sekolah, akunnya muncul di sini." />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Sekolah</TableHead>
                  <TableHead>Reseller</TableHead>
                  <TableHead className="text-right">Akun</TableHead>
                  <TableHead className="text-right">Nilai jual</TableHead>
                  <TableHead>Diminta</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {aktivasi.data.rows.map((r) => (
                  <TableRow key={r.school_id} data-testid={`billing-queue-row-${r.school_id}`}>
                    <TableCell>
                      <p className="font-medium">{r.school_name}</p>
                      <p className="text-xs text-muted-foreground">{r.dari_reseller ? "permintaan dari reseller" : "ditandai founder sendiri"}</p>
                    </TableCell>
                    <TableCell className="text-sm">{r.reseller_name}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.akun}</TableCell>
                    <TableCell className="text-right tabular-nums">{rupiah(r.nominal_jual)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" aria-hidden />{waktu(r.diminta_pada)}</span>
                      {r.diminta_oleh && <span className="block">{r.diminta_oleh}</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" data-testid={`billing-queue-activate-${r.school_id}`}
                        onClick={() => onActivate({
                          action: "ACTIVE",
                          scope: { school_id: r.school_id, from_status: "CONFIRMED" },
                          jumlah: r.akun, targetLabel: r.school_name, rincian: r.akun_detail,
                        })}>
                        <CheckCircle2 className="h-4 w-4 mr-1.5" /> Aktifkan {r.akun} akun
                        <ActionDot show={aktivasiBaru(r.school_name)} testId={`billing-queue-activate-dot-${r.school_id}`}
                          className="ml-1" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      <section className="rounded-2xl border bg-card p-6" data-testid="billing-queue-setoran">
        <div className="mb-4">
          <h3 className="flex items-center gap-2 text-base font-bold">
            <Landmark className="h-4 w-4 text-[#0B3D91]" aria-hidden /> 2. Verifikasi setoran kulakan reseller
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Reseller melaporkan sudah menyetor harga kulakan ke founder. Cocokkan dengan mutasi rekening/QRIS
            sebelum menekan Verifikasi — setelah diverifikasi, setoran dihitung mengurangi sisa kulakan.
          </p>
        </div>
        {setoran.isLoading ? <LoadingState rows={3} /> : setoran.error ? (
          <ErrorState error={setoran.error} onRetry={setoran.refetch} />
        ) : !menungguSetor.length ? (
          <Notice tone="ok" testId="billing-queue-setoran-empty">
            Tidak ada setoran yang menunggu verifikasi pada periode {periode}.
          </Notice>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reseller</TableHead>
                  <TableHead>Sekolah</TableHead>
                  <TableHead className="text-right">Jumlah setoran</TableHead>
                  <TableHead>Metode &amp; waktu</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {menungguSetor.map((s) => (
                  <TableRow key={s.id} data-testid={`billing-queue-setoran-${s.id}`}>
                    <TableCell className="font-medium">{s.reseller_name}</TableCell>
                    <TableCell className="text-sm">{s.rincian?.school_name || "Setoran umum"}</TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">{rupiah(s.amount_idr)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {s.metode} · {waktu(s.at)}{s.catatan ? ` · ${s.catatan}` : ""}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" disabled={busy === s.id}
                        onClick={() => verifikasi(s)} data-testid={`billing-queue-verify-${s.id}`}>
                        {busy === s.id ? <Spinner /> : <CheckCircle2 className="h-4 w-4 mr-1.5 text-emerald-600" />} Verifikasi
                        <ActionDot show={setoranBaru(s)} testId={`billing-queue-verify-dot-${s.id}`} className="ml-1" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>
    </div>
  );
}
